import React, { useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Text, Flex, Button } from 'rebass';
import useOutsideClick from 'use-onclickoutside';
import isEmpty from 'lodash/isEmpty';
import { useStoryDate, useStoryFromStoryShare, useIsStoryUnread } from '../../hooks';
import TextList from './text-list';

import StoryListItemCover from './cover';
import CollectionStoryOptions from 'collections/detail/story-options';
import useEntities from 'entities/hooks/use-entities';
import { updateStory } from 'stories/api';
import { bodyWithLockVersion } from 'app/form/helper';
import { getResourcePath, useJournalsChaptersSWR } from 'collections/api';
import EditingOptions from './EditingOptions';
import useEntity from 'entities/hooks/use-entity';
import Tippy from '@tippy.js/react';
import { TooltipWrap } from 'stories/story-detail-header/styled';
import BufferingOverlay from 'app/modal/BufferingOverlay';
import { isValidDateString } from 'stories/helper';

const Badge = () => (
  <Box
    bg="accent"
    py={1}
    px={2}
    color="defaultText"
    fontSize={0}
    css="border-radius: 25px; position: absolute; top: 16px; left: 16px;"
  >
    Updated
  </Box>
);

export function AddToChapterSection({ journalId, storyID, story, chapter_id, revalidate, children }) {
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const path = getResourcePath({
    ...{ id: journalId, type: 'child-journals' }
  });
  const { revalidate: revalidateJournal } = useSWR(`${path}${chapter_id ? `?chapter_id=${chapter_id}` : ''}`);

  const wrapper = useRef(null);

  useOutsideClick(wrapper, () => {
    setTooltipOpen(false)
  });

  return <Tippy
    content={
      <TooltipWrap ref={wrapper}>
        <BufferingOverlay isBuffering={isSubmitting} boxSx={{
          background: '#FFFFFF',
          borderRadius: '12.307px'
        }}>
          <Text fontFamily="Poppins" fontSize={18} py={2} lineHeight="165%" sx={{ borderBottom: '2px solid #f0f0f0' }} >{!!children ? 'Move' : 'Add'} to chapter:</Text>
          <ChapterList
            storyID={storyID}
            updateChapter={async (id) => {
              setIsSubmitting(true)
              await updateStory(storyID, bodyWithLockVersion(story, {
                'chapter-id': id
              }))
              if (revalidate)
                revalidate()
              setTooltipOpen(false)
              revalidateJournal()
              setIsSubmitting(false)
            }}
          />
        </BufferingOverlay>
      </TooltipWrap>
    }
    interactive
    theme="light"
    arrow
    trigger="manual"
    placement="bottom"
    visible={isTooltipOpen}
  >

    {children ? <Box onClick={() => setTooltipOpen(open => !open)}>
      {children}
    </Box> : <Button bg="transparent" sx={{ border: 0, p: 0, py: 1, width: '100%' }} onClick={() => setTooltipOpen(open => !open)}>
      {/* <Text as={LuBookMarked} color="rgba(0, 0, 0, 0.45)" size={20} /> */}
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M13.6 -0.00637817H2.4C1.76348 -0.00637817 1.15303 0.246478 0.702944 0.696566C0.252856 1.14665 0 1.7571 0 2.39362V13.5936C0 14.2301 0.252856 14.8406 0.702944 15.2907C1.15303 15.7408 1.76348 15.9936 2.4 15.9936H13.6C14.2365 15.9936 14.847 15.7408 15.2971 15.2907C15.7471 14.8406 16 14.2301 16 13.5936V2.39362C16 1.7571 15.7471 1.14665 15.2971 0.696566C14.847 0.246478 14.2365 -0.00637817 13.6 -0.00637817ZM6.4 1.59362H9.6V4.09762L8.44 3.29762C8.30938 3.2116 8.1564 3.16575 8 3.16575C7.8436 3.16575 7.69062 3.2116 7.56 3.29762L6.4 4.09762V1.59362ZM14.4 13.5936C14.4 13.8058 14.3157 14.0093 14.1657 14.1593C14.0157 14.3093 13.8122 14.3936 13.6 14.3936H2.4C2.18783 14.3936 1.98434 14.3093 1.83431 14.1593C1.68429 14.0093 1.6 13.8058 1.6 13.5936V2.39362C1.6 2.18145 1.68429 1.97797 1.83431 1.82794C1.98434 1.67791 2.18783 1.59362 2.4 1.59362H4.8V5.59362C4.80039 5.73835 4.84002 5.88025 4.91469 6.00423C4.98936 6.1282 5.09626 6.2296 5.224 6.29762C5.3491 6.36186 5.48892 6.39198 5.62937 6.38496C5.76982 6.37793 5.90593 6.33401 6.024 6.25762L8 4.95362L9.96 6.25762C10.0803 6.33686 10.2198 6.38214 10.3637 6.38867C10.5076 6.39521 10.6507 6.36276 10.7777 6.29475C10.9047 6.22674 11.011 6.1257 11.0853 6.00229C11.1596 5.87888 11.1993 5.73769 11.2 5.59362V1.59362H13.6C13.8122 1.59362 14.0157 1.67791 14.1657 1.82794C14.3157 1.97797 14.4 2.18145 14.4 2.39362V13.5936Z" fill="black" fill-opacity="0.45" />
      </svg>
    </Button>}
  </Tippy>
}


export const ChapterList = ({ updateChapter, storyID }) => {
  const allChapters = useEntities('chapter')
  const seen = new Set();

  const filtered = Object.fromEntries(
    Object.entries(allChapters).filter(([key, chapter]) => {
      if (seen.has(chapter.name)) return false;

      seen.add(chapter.name);
      return true;
    })
  );

  return <Box css="overflow-y: scroll;" mt={2} >
    {(Object.keys(filtered) || []).sort((a, b) => filtered[a]?.position - filtered[b]?.position).filter((id) => {
      const attributes = filtered[id];
      return attributes?.name !== 'Milestones' && attributes?.name !== 'Talking Family Tree' && attributes?.name !== 'Watch Me Grow' && attributes?.name !== 'Growth Chart'
    }).map((id, index) => {
      const attributes = filtered[id];
      const isOptionSelected = attributes?.stories.some((e) => e?.id === storyID);
      return (
        <Flex
          key={attributes.id}
          px={3}
          py={2}
          onClick={(e) => {
            e.preventDefault()
            updateChapter(id, attributes?.name)
          }}
          bg={isOptionSelected ? '#F5EEF8' : 'white'}
          sx={{ borderRadius: '100px', cursor: 'pointer' }}
        >
          <Text fontSize="14px" fontFamily="Poppins" mr={1} color={isOptionSelected ? "#9B59B6" : "#64748B"}>
            {index + 1}.
          </Text>
          <Text fontSize="14px" fontFamily="Poppins" fontWeight={500} lineHeight="165%" letterSpacing="0.14px" color={isOptionSelected ? "#9B59B6" : "#64748B"}>
            {attributes?.name}
          </Text>
        </Flex>
      );
    })}
  </Box>
}

const StoryListItem = ({ id, type, collectionsList }) => {
  const { push } = useHistory();
  const { story } = useStoryFromStoryShare({ id, type });
  const { chapter_id } = useParams()
  const storyDate = useStoryDate(story);
  const hasBadge = useIsStoryUnread(story.id);

  const [chapterName, setChapterName] = useState(null);
  const [showOptions, setShowOptions] = useState(false)
  const journalId = story?.['journal-ids']?.[0] || null
  const { revalidate } = useJournalsChaptersSWR(journalId);

  const path = getResourcePath({
    ...{ id: journalId, type: 'child-journals' }
  });
  const { revalidate: revalidateJournal } = useSWR(`${path}${chapter_id ? `?chapter_id=${chapter_id}` : ''}`);


  const onClick = useCallback(() => {
    const pathname = story && `/story/${story.id}/edit`;
    push({ pathname });
  }, [push, story]);


  const handleEditSettingClick = () => {
    push(`/story/${story?.id}/edit/settings`);
  };
  const updateChapter = async (id, name) => {
    setChapterName(name)
    await updateStory(story?.id, bodyWithLockVersion(story, {
      'chapter-id': id
    }))
    revalidate()
    revalidateJournal()
  }

  const { entity: currentChapter } = useEntity({ id: story?.chapter?.id, type: 'chapter' });

  useEffect(() => {
    if (currentChapter?.name) {
      setChapterName(currentChapter?.name)
    }
  }, [currentChapter?.name])

  if (!story || isEmpty(story)) return false;

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      width="100%"
      sx={{
        position: 'relative',
        height: '100%',
        bg: 'white',
        boxShadow: 'small',
        borderRadius: 12,
        border: '1px solid #F0F0F0'
      }}
    >
      {showOptions && <CollectionStoryOptions currentStoryId={story.id} onClose={() => setShowOptions(false)} />}
      {hasBadge && <Badge />}
      <Button
        type="button"
        onClick={onClick}
        variant="unset"
        css="overflow: hidden; width: 100%; text-align: left;"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          borderRadius: 12,
        }}
      >
        <StoryListItemCover id={story.id} />
        <Flex flexDirection="column" flex="1">
          <Text p={12} color="mutedText" fontFamily="Poppins" pb={0}>
            {isValidDateString(story.title)}
          </Text>
          <Text p={12} py={1} color="lightText" fontFamily="Poppins" fontSize={0}>
            {storyDate}
          </Text>
          {!!collectionsList.length && <TextList title="in: " items={collectionsList} />}
        </Flex>
      </Button>
      <Flex justifyContent="space-around" p="12px" alignItems="center"
        sx={{
          boxShadow: '0px 1px 0px 0px #F0F0F0 inset'
        }}
      >
        <EditingOptions
          chapterName={chapterName}
          story={story}
          handleEditSettingClick={handleEditSettingClick}
          updateChapter={updateChapter}
        />
      </Flex>
      {/* <Flex justifyContent="space-evenly" alignItems="center"
        sx={{
          boxShadow: '0px 1px 0px 0px #F0F0F0 inset'
        }}
      >
        <Button bg="transparent" sx={{ border: 0, p: 0, py: 1, width: '100%' }}
          onClick={() => {
            const pathname = story && `/story/${story.id}/edit/settings`;
            push({ pathname });
          }}
        >
          <Text as={BsBookmarkStar} color="rgba(0, 0, 0, 0.45)" size={20} />
        </Button>

        <Text sx={{
          height: '16px',
          borderRight: '2px solid #F0F0F0'
        }} />

        <Button bg="transparent" sx={{ border: 0, p: 0, py: 1, width: '100%' }}
          onClick={() => {
            const pathname = story && `/story/${story.id}/edit`;
            push({ pathname });
          }}
        >
          <Text as={AiOutlineEdit} color="rgba(0, 0, 0, 0.45)" size={20} />
        </Button>

        <Text sx={{
          height: '16px',
          borderRight: '2px solid #F0F0F0'
        }} />

        <Tippy
          content={
            <TooltipWrap ref={wrapper}>
              <Text fontFamily="Poppins" fontSize={18} py={2} lineHeight="165%" sx={{
                boxShadow: '0px 1px 0px 0px #F0F0F0'
              }}>Add to chapter:</Text>
              <ChapterList
                storyID={story?.id}
                updateChapter={updateChapter}
              />
            </TooltipWrap>
          }
          interactive
          theme="light"
          arrow
          trigger="manual"
          placement="bottom-end"
    visible={isTooltipOpen}
        >

          <Button bg="transparent" sx={{ border: 0, p: 0, py: 1, width: '100%' }} onClick={() => setTooltipOpen(open => !open)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.6 -0.00637817H2.4C1.76348 -0.00637817 1.15303 0.246478 0.702944 0.696566C0.252856 1.14665 0 1.7571 0 2.39362V13.5936C0 14.2301 0.252856 14.8406 0.702944 15.2907C1.15303 15.7408 1.76348 15.9936 2.4 15.9936H13.6C14.2365 15.9936 14.847 15.7408 15.2971 15.2907C15.7471 14.8406 16 14.2301 16 13.5936V2.39362C16 1.7571 15.7471 1.14665 15.2971 0.696566C14.847 0.246478 14.2365 -0.00637817 13.6 -0.00637817ZM6.4 1.59362H9.6V4.09762L8.44 3.29762C8.30938 3.2116 8.1564 3.16575 8 3.16575C7.8436 3.16575 7.69062 3.2116 7.56 3.29762L6.4 4.09762V1.59362ZM14.4 13.5936C14.4 13.8058 14.3157 14.0093 14.1657 14.1593C14.0157 14.3093 13.8122 14.3936 13.6 14.3936H2.4C2.18783 14.3936 1.98434 14.3093 1.83431 14.1593C1.68429 14.0093 1.6 13.8058 1.6 13.5936V2.39362C1.6 2.18145 1.68429 1.97797 1.83431 1.82794C1.98434 1.67791 2.18783 1.59362 2.4 1.59362H4.8V5.59362C4.80039 5.73835 4.84002 5.88025 4.91469 6.00423C4.98936 6.1282 5.09626 6.2296 5.224 6.29762C5.3491 6.36186 5.48892 6.39198 5.62937 6.38496C5.76982 6.37793 5.90593 6.33401 6.024 6.25762L8 4.95362L9.96 6.25762C10.0803 6.33686 10.2198 6.38214 10.3637 6.38867C10.5076 6.39521 10.6507 6.36276 10.7777 6.29475C10.9047 6.22674 11.011 6.1257 11.0853 6.00229C11.1596 5.87888 11.1993 5.73769 11.2 5.59362V1.59362H13.6C13.8122 1.59362 14.0157 1.67791 14.1657 1.82794C14.3157 1.97797 14.4 2.18145 14.4 2.39362V13.5936Z" fill="black" fill-opacity="0.45" />
            </svg>
          </Button>
        </Tippy>
      </Flex> */}
    </Flex >
  );
};

StoryListItem.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  children: PropTypes.node,
  collectionsList: PropTypes.arrayOf(PropTypes.string),
};
StoryListItem.defaultProps = {
  children: null,
  collectionsList: [],
};

export default StoryListItem;
